import React, { useRef } from 'react';
import { Box, Typography, IconButton, LinearProgress } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.log,.csv';
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 5;

const ALLOWED_EXTENSIONS = new Set(
  ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'txt', 'log', 'csv']
);

function getExtension(name) {
  if (!name) return '';
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileAttachmentZone({
  files = [],
  onChange,
  error,
  disabled,
  uploadProgress,
  maxFiles = MAX_FILES,
  label = 'Attachments (optional, max 5 files, 10 MB each)',
}) {
  const inputRef = useRef(null);

  const validateFile = (file) => {
    const ext = getExtension(file.name);
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return `File type not allowed: ${file.name}. Use images, PDF, Word, text, log, or CSV.`;
    }
    if (file.size > MAX_SIZE_BYTES) {
      return `${file.name} exceeds 10 MB.`;
    }
    return null;
  };

  const handleSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    if (selected.length === 0) return;
    const next = [...files];
    const errs = [];
    for (const file of selected) {
      if (next.length >= maxFiles) {
        errs.push(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed.`);
        break;
      }
      const msg = validateFile(file);
      if (msg) errs.push(msg);
      else next.push(file);
    }
    onChange(next, errs.length ? errs : null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    const items = e.dataTransfer?.files;
    if (!items?.length) return;
    const selected = Array.from(items);
    const next = [...files];
    const errs = [];
    for (const file of selected) {
      if (next.length >= maxFiles) {
        errs.push(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed.`);
        break;
      }
      const msg = validateFile(file);
      if (msg) errs.push(msg);
      else next.push(file);
    }
    onChange(next, errs.length ? errs : null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const remove = (index) => {
    const next = files.filter((_, i) => i !== index);
    onChange(next, null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => !disabled && inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          p: 2,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: 'action.hover',
        }}
        role="button"
        aria-label="Add files (drag and drop or click)"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={handleSelect}
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
          aria-label="Choose files"
        />
        <AttachFileIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Drag and drop or click to browse. Images, PDF, Word, text, log, CSV.
        </Typography>
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }} role="alert">
          {Array.isArray(error) ? error.join(' ') : error}
        </Typography>
      )}
      {files.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {files.map((file, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {file.name} ({formatSize(file.size)})
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); remove(i); }}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {uploadProgress != null && uploadProgress < 100 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">Uploading…</Typography>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 0.5 }} />
        </Box>
      )}
    </Box>
  );
}

export { MAX_FILES, MAX_SIZE_BYTES };
